import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Users, 
  Settings, 
  CheckCircle, 
  ArrowRight,
  Database,
  Shield,
  Zap,
  Target,
  BarChart3,
  Globe,
  Clock,
  MessageSquare,
  GraduationCap,
  Heart
} from 'lucide-react';

const Dynamics365Implementation: React.FC = () => {
  const dynamics365Modules = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Sales",
      description: "Microsoft Dynamics 365 for Sales is a powerful Customer Relationship Management (CRM) solution designed to optimize your sales processes. It focuses on managing the sales pipeline effectively while offering full visibility into customer interactions and relationship activities.",
      features: ["Sales pipeline management", "Customer interaction tracking", "Customizable automation", "Business rule enforcement", "Guided process flows", "Deal closing optimization"]
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Marketing",
      description: "Dynamics 365 for Marketing is a comprehensive marketing automation platform that empowers organizations to nurture leads and seamlessly guide prospects through the buyer's journey from initial interest to becoming loyal customers.",
      features: ["Lead nurturing", "Marketing automation", "Buyer journey mapping", "Built-in analytics", "Sales integration", "Data-driven decisions"]
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Field Service",
      description: "Dynamics 365 Field Service is a powerful business application designed to help organizations efficiently deliver onsite service to customer locations. It leverages workflow automation and intelligent scheduling algorithms.",
      features: ["Onsite service delivery", "Workflow automation", "Intelligent scheduling", "Mobile capabilities", "Customer satisfaction", "Service performance"]
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Customer Service",
      description: "Microsoft Dynamics 365 Customer Service is a comprehensive solution designed to streamline customer service operations. It automates case and knowledge management, delivering personalized support.",
      features: ["Case management", "Knowledge management", "360-degree customer view", "Real-time dashboards", "Performance monitoring", "Service quality improvement"]
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Education",
      description: "Microsoft Dynamics 365 Education Accelerator provides a tailored industry data model designed to enhance educational solutions across the Microsoft Dataverse and broader Microsoft technology stack.",
      features: ["Student management", "School administration", "Staff tracking", "Faculty management", "Connected experiences", "Data-driven education"]
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Healthcare",
      description: "Microsoft Dynamics 365 for Healthcare empowers healthcare organizations to manage all aspects of their operations from a centralized platform. It offers a comprehensive 360-degree view of patient information.",
      features: ["Patient information management", "Document management", "Task coordination", "Performance metrics", "Better coordination", "Enhanced patient care"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Finance & Accounting",
      description: "Dynamics 365 Finance and Accounting is a comprehensive solution designed for large, global enterprises. It encompasses budgeting, project management, financial operations, and accounting.",
      features: ["Budgeting", "Project management", "Financial operations", "Real-time insights", "Multi-region support", "Multi-currency support"]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Mixed Reality",
      description: "Accelerate learning, ensure process consistency, and minimize errors by providing clear, step-by-step instructions. Empower your workforce to stay safe and productive on the job.",
      features: ["Learning acceleration", "Process consistency", "Error minimization", "Step-by-step instructions", "Workforce safety", "Productivity improvement"]
    }
  ];

  const benefits = [
    "Tailored solutions for small and medium-sized organizations",
    "Comprehensive Dynamics 365 implementation across all modules",
    "Expert guidance from certified Microsoft professionals",
    "Seamless integration with existing business processes",
    "Ongoing support and maintenance services",
    "Training and knowledge transfer for your team"
  ];

  const implementationSteps = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We analyze your business requirements and create a comprehensive implementation plan."
    },
    {
      step: "02",
      title: "System Design",
      description: "Customize Dynamics 365 to match your specific business processes and workflows."
    },
    {
      step: "03",
      title: "Development & Configuration",
      description: "Build custom solutions and configure the system according to your needs."
    },
    {
      step: "04",
      title: "Testing & Quality Assurance",
      description: "Thorough testing to ensure everything works perfectly before go-live."
    },
    {
      step: "05",
      title: "Deployment & Go-Live",
      description: "Smooth deployment with minimal disruption to your business operations."
    },
    {
      step: "06",
      title: "Training & Support",
      description: "Comprehensive training for your team and ongoing support for success."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
             {/* Hero Section */}
       <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
                         <div className="flex items-center justify-center mb-6">
               <Building2 className="w-12 h-12 mr-4" />
               <h1 className="text-5xl font-bold">Dynamics 365 Services</h1>
             </div>
             <p className="text-xl text-blue-100 mb-8">
               Empowering Businesses with Tailored Solutions Across Sales, Marketing, Service, and Beyond. 
               CRMONCE delivers comprehensive Dynamics 365 implementation services for small and medium-sized organizations.
             </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
            >
              Get Started Today
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container-custom">
                     <motion.div
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             viewport={{ once: true }}
             className="text-center mb-16"
           >
             <h2 className="text-4xl font-bold text-gray-900 mb-6">
               Dynamics 365 Modules
             </h2>
             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
               Comprehensive Dynamics 365 solutions tailored to your industry and business needs. 
               We implement and customize all major Dynamics 365 modules for optimal performance.
             </p>
           </motion.div>

           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
             {dynamics365Modules.map((module, index) => (
                             <motion.div
                 key={index}
                 initial={{ opacity: 0, y: 30 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.6, delay: index * 0.1 }}
                 viewport={{ once: true }}
                 className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
               >
                 <div className="text-blue-600 mb-4">{module.icon}</div>
                 <h3 className="text-xl font-semibold text-gray-900 mb-3">
                   {module.title}
                 </h3>
                 <p className="text-gray-600 mb-6">
                   {module.description}
                 </p>
                 <div className="space-y-2">
                   {module.features.map((feature, featureIndex) => (
                     <div key={featureIndex} className="flex items-center space-x-2">
                       <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                       <span className="text-gray-700 text-sm">{feature}</span>
                     </div>
                   ))}
                 </div>
               </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
                         <h2 className="text-4xl font-bold text-gray-900 mb-6">
               Why Choose CRMONCE for Dynamics 365?
             </h2>
             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
               As a professional services firm committed to delivering business solutions to small and medium-sized organizations, 
               we provide tailored Dynamics 365 implementation services.
             </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

             {/* Additional Services */}
       <section className="py-20 bg-gray-50">
         <div className="container-custom">
           <motion.div
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             viewport={{ once: true }}
             className="text-center mb-16"
           >
             <h2 className="text-4xl font-bold text-gray-900 mb-6">
               Additional Services
             </h2>
             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
               Beyond Dynamics 365 implementation, CRMONCE offers comprehensive training and cloud services 
               to support your digital transformation journey.
             </p>
           </motion.div>

           <div className="grid md:grid-cols-3 gap-8">
             <motion.div
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.6, delay: 0.1 }}
               viewport={{ once: true }}
               className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
             >
               <div className="text-blue-600 mb-4">
                 <GraduationCap className="w-8 h-8" />
               </div>
               <h3 className="text-xl font-semibold text-gray-900 mb-3">
                 Training Programs
               </h3>
               <p className="text-gray-600 mb-4">
                 Comprehensive training for Dynamics 365, Power Platform, Azure, and more.
               </p>
               <ul className="space-y-2 text-sm text-gray-600">
                 <li>• Dynamics 365 Training</li>
                 <li>• Power Platform Training</li>
                 <li>• Azure Developer Training</li>
                 <li>• Office 365 Training</li>
                 <li>• C# Dot Net Training</li>
               </ul>
             </motion.div>

             <motion.div
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.6, delay: 0.2 }}
               viewport={{ once: true }}
               className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
             >
               <div className="text-blue-600 mb-4">
                 <Globe className="w-8 h-8" />
               </div>
               <h3 className="text-xl font-semibold text-gray-900 mb-3">
                 Cloud Services
               </h3>
               <p className="text-gray-600 mb-4">
                 Complete cloud solutions including Azure, Microsoft 365, and Power Platform.
               </p>
               <ul className="space-y-2 text-sm text-gray-600">
                 <li>• Azure Cloud Services</li>
                 <li>• Microsoft 365 Implementation</li>
                 <li>• Power Automate Solutions</li>
                 <li>• Power Apps Development</li>
                 <li>• Data Migration & Integration</li>
               </ul>
             </motion.div>

             <motion.div
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.6, delay: 0.3 }}
               viewport={{ once: true }}
               className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
             >
               <div className="text-blue-600 mb-4">
                 <Users className="w-8 h-8" />
               </div>
               <h3 className="text-xl font-semibold text-gray-900 mb-3">
                 Support & Maintenance
               </h3>
               <p className="text-gray-600 mb-4">
                 Ongoing support and maintenance services to ensure your systems run smoothly.
               </p>
               <ul className="space-y-2 text-sm text-gray-600">
                 <li>• 24/7 Technical Support</li>
                 <li>• System Maintenance</li>
                 <li>• Performance Optimization</li>
                 <li>• Security Updates</li>
                 <li>• User Training & Support</li>
               </ul>
             </motion.div>
           </div>
         </div>
       </section>

       {/* Implementation Process */}
       <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
                         <h2 className="text-4xl font-bold text-gray-900 mb-6">
               Our Implementation Methodology
             </h2>
             <p className="text-xl text-gray-600 max-w-3xl mx-auto">
               CRMONCE follows a proven, step-by-step approach to ensure successful Dynamics 365 implementation 
               and maximum return on investment for your organization.
             </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {implementationSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
                         <h2 className="text-4xl font-bold mb-6">
               Ready to Transform Your Business with Dynamics 365?
             </h2>
             <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
               Contact CRMONCE today for expert Dynamics 365 implementation services. 
               Call us for fast support or email us for queries.
             </p>
                         <div className="flex flex-col sm:flex-row gap-4 justify-center">
               <motion.button
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
                 className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
               >
                 Call +91 8096556344
               </motion.button>
               <motion.button
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
                 className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
               >
                 Email info@crmonce.com
               </motion.button>
             </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Dynamics365Implementation; 